import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MagnifyingGlass, List, X } from "@phosphor-icons/react";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import SearchOverlay from "./SearchOverlay";

const NavLink = ({ to, label, active }) => (
  <Link
    to={to}
    data-testid={`nav-link-${label.toLowerCase().replace(/\s/g, "-")}`}
    className="relative font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:text-white"
    style={{ color: active ? "#fff" : "#94A3B8" }}
  >
    {label}
    {active && (
      <motion.span
        layoutId="nav-dot"
        className="absolute -bottom-2 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-cyan"
      />
    )}
  </Link>
);

export default function Navbar() {
  const { t, lang, toggle } = useLang();
  const { user, logout } = useAuth();
  const { sponsorsEnabled, liveEnabled, liveScheduledAt } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/", label: t("home") },
    { to: "/program", label: t("program") },
    { to: "/library/video", label: t("video") },
    { to: "/library/presentation", label: t("presentations") },
    { to: "/library/other", label: t("other") },
    ...(sponsorsEnabled ? [{ to: "/supported-by", label: t("supported_by") }] : []),
  ];

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        data-testid="navbar"
        className="fixed top-0 left-0 z-50 w-full transition-colors duration-500"
        style={{
          backgroundColor: scrolled ? "rgba(10,15,26,0.72)" : "rgba(10,15,26,0)",
          backdropFilter: scrolled ? "blur(20px)" : "none",
          borderBottom: scrolled ? "1px solid rgba(255,255,255,0.08)" : "1px solid transparent",
        }}
      >
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-5 md:px-10">
          <Link to="/" data-testid="brand-logo" className="group flex items-center gap-3">
            <img
              src="/logos/logo_navbar.png"
              alt="Wall Board Academy"
              className="h-11 w-11 object-contain transition-all duration-500 group-hover:drop-shadow-[0_0_14px_rgba(0,240,255,0.5)]"
            />
            <span className="font-head text-sm font-extrabold uppercase leading-none tracking-[0.18em] text-white">
              Wall Board<br />
              <span className="text-cyan">Academy</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-9 lg:flex">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} label={l.label} active={isActive(l.to)} />
            ))}
          </nav>

          <div className="flex items-center gap-3 md:gap-5">
            {liveEnabled ? (
              <Link
                data-testid="live-nav-btn"
                to="/live"
                className="flex items-center gap-2 border border-red-500/60 bg-red-500/10 px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-widest text-red-400 transition-colors hover:bg-red-500 hover:text-white"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                </span>
                Live
              </Link>
            ) : liveScheduledAt && new Date(liveScheduledAt).getTime() > Date.now() ? (
              <Link
                data-testid="live-nav-btn"
                to="/live"
                className="flex items-center gap-2 border border-cyan/50 bg-cyan/5 px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-widest text-cyan transition-colors hover:bg-cyan hover:text-ink-900"
              >
                <span className="inline-flex h-2 w-2 rounded-full bg-cyan" />
                Live · Prossima
              </Link>
            ) : null}
            <button
              data-testid="search-trigger"
              onClick={() => setSearchOpen(true)}
              className="text-slate-300 transition-colors hover:text-cyan"
              aria-label="Search"
            >
              <MagnifyingGlass size={22} weight="light" />
            </button>

            <button
              data-testid="lang-toggle"
              onClick={toggle}
              className="flex items-center gap-1 border border-cyan/40 px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest text-cyan transition-colors hover:bg-cyan hover:text-ink-900"
            >
              <span className={lang === "en" ? "text-white" : "opacity-50"}>EN</span>
              <span className="opacity-30">/</span>
              <span className={lang === "it" ? "text-white" : "opacity-50"}>IT</span>
            </button>

            {user ? (
              <div className="hidden items-center gap-3 md:flex">
                {user.role === "admin" && (
                  <Link data-testid="admin-link" to="/admin" className="font-mono text-xs uppercase tracking-widest text-slate-300 hover:text-cyan">
                    {t("admin")}
                  </Link>
                )}
                <Link data-testid="account-link" to="/account" className="font-mono text-xs uppercase tracking-widest text-slate-300 hover:text-cyan">
                  {t("account")}
                </Link>
                <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }} className="font-mono text-xs uppercase tracking-widest text-slate-500 hover:text-white">
                  {t("logout")}
                </button>
              </div>
            ) : (
              <div className="hidden items-center gap-4 md:flex">
                <Link data-testid="login-link" to="/login" className="font-mono text-xs uppercase tracking-widest text-slate-300 hover:text-cyan">
                  {t("login")}
                </Link>
                <Link data-testid="register-link" to="/register" className="bg-cyan/90 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-widest text-ink-900 transition-colors hover:bg-cyan">
                  {t("register")}
                </Link>
              </div>
            )}

            <button className="text-slate-200 lg:hidden" onClick={() => setMobileOpen((v) => !v)} aria-label="Menu">
              {mobileOpen ? <X size={24} /> : <List size={24} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.nav
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden border-t border-white/10 bg-ink-800/95 backdrop-blur-xl lg:hidden"
            >
              <div className="flex flex-col gap-5 px-6 py-6">
                {links.map((l) => (
                  <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="font-mono text-sm uppercase tracking-widest text-slate-200">
                    {l.label}
                  </Link>
                ))}
                {user ? (
                  <>
                    {user.role === "admin" && (
                      <Link to="/admin" onClick={() => setMobileOpen(false)} className="font-mono text-sm uppercase tracking-widest text-cyan">{t("admin")}</Link>
                    )}
                    <Link to="/account" onClick={() => setMobileOpen(false)} className="font-mono text-sm uppercase tracking-widest text-slate-200">{t("account")}</Link>
                    <button onClick={() => { logout(); setMobileOpen(false); navigate("/"); }} className="text-left font-mono text-sm uppercase tracking-widest text-slate-500">{t("logout")}</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setMobileOpen(false)} className="font-mono text-sm uppercase tracking-widest text-slate-200">{t("login")}</Link>
                    <Link to="/register" onClick={() => setMobileOpen(false)} className="font-mono text-sm uppercase tracking-widest text-cyan">{t("register")}</Link>
                  </>
                )}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </motion.header>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
